'use client';

import * as Sentry from '@sentry/nextjs';
import Link from 'next/link';
import { useEffect } from 'react';
import { isChunkLoadError, maybeReloadOnChunkError } from '@/lib/chunk-reload';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Stale-deploy chunk failures self-heal with one reload — recover silently
  // (this class is already dropped from Sentry) before falling back to the page.
  const recovering = maybeReloadOnChunkError(error);

  useEffect(() => {
    if (recovering || isChunkLoadError(error)) return;
    Sentry.captureException(error);
  }, [error, recovering]);

  if (recovering) return null;
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
      <h2 className="font-heading text-page-title text-text-heading">Something went wrong</h2>
      <p className="mb-6 mt-2 max-w-md font-body text-body-sm text-text-muted">
        An unexpected error occurred. Please try again or return to the dashboard.
      </p>
      <div className="flex max-w-full flex-wrap justify-center gap-3">
        <button
          onClick={reset}
          className="min-w-0 rounded-control bg-statura-green-accessible px-4 py-2 font-body text-body-sm font-medium text-white transition-interactive duration-200 hover:bg-statura-green-accessible-hover"
        >
          Try again
        </button>
        <Link
          href="/overview"
          className="min-w-0 rounded-lg border border-control-border px-4 py-2 font-body text-body-sm font-medium text-text-body transition-interactive duration-200 hover:bg-surface-soft"
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
